import { Segmented } from 'antd'
import clsx from 'clsx'
import { type FC } from 'react'

import { DatePicker } from '@/components/elements'
import { DESCRIPTIONS, LABELS } from '@/constants/common/message.constants'
import {
  isClosingDateBasis,
  SEARCH_DATE_BASIS,
  SEARCH_DATE_BASIS_LABEL,
  type SearchDateBasis
} from '@/features/shared/models'

type Props = {
  /** SearchFormItem の htmlFor と対にする id */
  id?: string
  /** 現在の検索基準 */
  value: SearchDateBasis
  onChange: (value: SearchDateBasis) => void
  /** 締日基準（請求月/支払月）モード時の対象年月。DEFAULT 時は無視される */
  yearMonth: Date | null
  onYearMonthChange: (date: Date | null) => void
  /**
   * 請求月（INCOME_CLOSING_DATE）の選択肢を表示するか。
   * 運行実績一覧のパートナーユーザーは請求の情報を見せないため false（#5945）。
   */
  showIncome?: boolean
  /**
   * 支払月（EXPENSE_CLOSING_DATE）の選択肢を表示するか。
   * 配送依頼一覧は請求月のみのため false、運行実績一覧は 3 値のため true。
   */
  showExpense?: boolean
  disabled?: boolean
  className?: string
}

// 表示順は「通常 → 請求月 → 支払月」で固定。請求月は showIncome、支払月は showExpense のときのみ含める。
const buildOptions = (showIncome: boolean, showExpense: boolean) => {
  const bases: SearchDateBasis[] = [
    SEARCH_DATE_BASIS.DEFAULT,
    ...(showIncome ? [SEARCH_DATE_BASIS.INCOME_CLOSING_DATE] : []),
    ...(showExpense ? [SEARCH_DATE_BASIS.EXPENSE_CLOSING_DATE] : [])
  ]
  return bases.map((basis) => ({
    label: SEARCH_DATE_BASIS_LABEL[basis],
    value: basis
  }))
}

/**
 * 一覧検索の日付基準切替（通常 / 締日基準・請求月 / 締日基準・支払月）。VOC_PJ-939 #4387、設計 §4 D4/D8/D10。
 * 締日基準モードのときのみ年月ピッカー（picker="month"）を表示する。
 */
export const SearchDateBasisSegmented: FC<Props> = ({
  id,
  value,
  onChange,
  yearMonth,
  onYearMonthChange,
  showIncome = true,
  showExpense = false,
  disabled,
  className
}) => {
  const showYearMonthPicker = isClosingDateBasis(value)

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <Segmented
        id={id}
        value={value}
        disabled={disabled}
        aria-label={LABELS.SEARCH_DATE_BASIS}
        options={buildOptions(showIncome, showExpense)}
        onChange={(next) => onChange(next as SearchDateBasis)}
      />
      {showYearMonthPicker && (
        <DatePicker
          picker="month"
          format="yyyy-MM"
          value={yearMonth}
          disabled={disabled}
          placeholder={DESCRIPTIONS.SELECT_YEAR_MONTH}
          aria-label={LABELS.SEARCH_DATE_BASIS}
          onChange={(date) => onYearMonthChange(date ?? null)}
        />
      )}
    </div>
  )
}
